'use client';

import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Typography } from '@mui/material';
import { sectionSpacingCss } from '../section-spacing';

const practices = [
  {
    title: 'Moments matter, big & small',
    description:
      'Life’s not just about big goals—it’s about those little moments too. We celebrate them as wins, helping you grow with each step, no matter how small.',
    align: 'left',
  },
  {
    title: 'Reciprocal learning',
    description:
      'Coaching’s a two-way street—we learn from each other, and everyone brings something valuable to the table. No hierarchies here, just real, shared growth.',
    align: 'right',
  },
  {
    title: 'The power of narrative',
    description:
      'How you see yourself is shaped by the stories you tell. Let’s flip the script and explore new ways of thinking, unlocking fresh perspectives and possibilities.',
    align: 'left',
  },
  {
    title: 'Your journey, your way',
    description:
      'Everyone’s path is different, so we tailor every coaching experience to fit you. We meet you where you’re at and adjust as you go.',
    align: 'right',
  },
];

const Container = styled('section')`
  color: #fff;
  position: relative;
  overflow: hidden;
  margin-top: 80px;
  @media (max-width: 900px) {
    padding: 3rem 1rem;
  }
`;

const Inner = styled('div')`
  max-width: 1380px;
  margin: 0 auto;
`;

const Title = styled(Typography)`
  font-family: var(--font-cooper-light), sans-serif;
  font-size: 50px;
  line-height: 1.05;
  color: #ffffff;
  text-align: center;
  margin-bottom: 2.5rem;
`;

const Grid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  position: relative;
  z-index: 10;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const Item = styled(motion.div)<{ align?: string }>`
  display: block;
  text-align: ${p => (p.align === 'left' ? 'left' : 'right')};
  padding: 0 6rem;
  /* Nudge the 1st row down a bit on desktop so it clears the lower circles */
  &:nth-of-type(n + 1) {
    margin-right: 2rem;
    margin-left: ${p => (p.align === 'left' ? '0' : '2rem')};
  }

  @media (max-width: 900px) {
    /* Increase inner padding without changing spacing between cards */
    padding: 2rem;
    text-align: left;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 12px;
    margin-bottom: 1rem;
    border: 1px solid rgba(255, 255, 255, 0.04);
    box-shadow: 0 6px 18px rgba(7, 7, 10, 0.18);
    position: relative;
    z-index: 20;
    /* Reset extra spacing when stacked on mobile */
    &:nth-of-type(n + 3) {
      margin-top: 0;
    }
  }

  @media (max-width: 480px) {
    padding: 2rem;
  }
`;

const ItemTitle = styled(Typography)`
  font-weight: 700;
  font-size: 28px;

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const ItemText = styled(Typography)`
  color: rgba(255, 255, 255, 0.85);
  font-size: 18px;
  line-height: 1.5;
  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const SvgOverlay = styled('svg')`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  top: 0;
  left: 0;
  overflow: visible;

  /* Scale up on desktop to occupy more visual space without altering relative geometry */
  transform-origin: 50% 35%;
  transform: scale(1.35) translateY(-4%);

  /* push curves lower on small screens so they don't overlap text */
  @media (max-width: 900px) {
    transform: translateY(14%);
  }

  @media (max-width: 480px) {
    transform: translateY(24%);
  }

  @media (max-width: 900px) {
    display: none;
  }

  /* slightly reduce stroke thickness on tiny screens */
  @media (max-width: 480px) {
    & path {
      stroke-width: 1.5;
    }
    & circle {
      stroke-width: 4;
    }
  }
`;

export default function OurPractices({ data }: { data?: any }): React.ReactElement {
  const items = data?.practices && Array.isArray(data.practices) ? data.practices : practices;

  return (
    <Container>
      <Inner>
        <Title variant="h3">{data?.heading || 'Our practices'}</Title>

        <Grid>
          {items.map((practice: any, index: number) => (
            <Item
              key={index}
              align={practice.align || (index % 2 === 0 ? 'left' : 'right')}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.12 }}
              viewport={{ once: true }}
            >
              <ItemTitle variant="subtitle1">{practice.title}</ItemTitle>
              <ItemText variant="body2">{practice.description}</ItemText>
            </Item>
          ))}
        </Grid>

        {/* Decorative intersecting bell curves with dots at ends */}
        <SvgOverlay
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 800 600"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden
        >
          <defs>
            <filter id="pulseGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="0" result="blur">
                <animate
                  attributeName="stdDeviation"
                  values="0;6;0"
                  dur="4s"
                  repeatCount="indefinite"
                />
              </feGaussianBlur>
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <g transform="translate(0,120)">
            <path d="M300,350 Q400,100 500,350" stroke="#C77DFF" strokeWidth="2" fill="none" />
            {/* purple: glow stroke beneath, filled circle on top */}
            <circle
              cx="300"
              cy="350"
              r="8"
              fill="none"
              stroke="#C77DFF"
              strokeWidth="6"
              filter="url(#pulseGlow)"
            >
              <animate
                attributeName="stroke-opacity"
                values="0.0;1;0.0"
                dur="4s"
                repeatCount="indefinite"
              />
            </circle>
            <circle cx="300" cy="350" r="8" fill="#C77DFF" />

            <circle
              cx="500"
              cy="350"
              r="8"
              fill="none"
              stroke="#FFF"
              strokeWidth="6"
              filter="url(#pulseGlow)"
            >
              <animate
                attributeName="stroke-opacity"
                values="0.0;1;0.0"
                dur="4s"
                repeatCount="indefinite"
              />
            </circle>
            <circle cx="500" cy="350" r="8" fill="#FFF" />
          </g>

          {/* Wider green curve with higher peak */}
          <path d="M250,250 Q400,550 550,250" stroke="#7CFFCB" strokeWidth="2" fill="none" />
          {/* green: glow stroke beneath, filled circle on top */}
          <circle
            cx="250"
            cy="250"
            r="8"
            fill="none"
            stroke="#7CFFCB"
            strokeWidth="6"
            filter="url(#pulseGlow)"
          >
            <animate
              attributeName="stroke-opacity"
              values="0.0;1;0.0"
              dur="4s"
              repeatCount="indefinite"
            />
          </circle>
          <circle cx="250" cy="250" r="8" fill="#7CFFCB" />

          <circle
            cx="550"
            cy="250"
            r="8"
            fill="none"
            stroke="#7CFFCB"
            strokeWidth="6"
            filter="url(#pulseGlow)"
          >
            <animate
              attributeName="stroke-opacity"
              values="0.0;1;0.0"
              dur="4s"
              repeatCount="indefinite"
            />
          </circle>
          <circle cx="550" cy="250" r="8" fill="#7CFFCB" />
        </SvgOverlay>
      </Inner>
    </Container>
  );
}
